import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { xpApi, participantAuth } from "@/lib/experienceApi";
import { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Trophy, CheckCircle2, XCircle } from "lucide-react";
import axios from "axios";

/** Full-quality quiz page — reuses Academy engine when the wine has quiz_id
 * wired. Falls back to a stub 3-question QCM when unlinked. Grades server-side
 * via /experience/wines/quiz-submit. */
export default function ExperienceQuiz() {
  const { wineId } = useParams();
  const nav = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orderState, setOrderState] = useState({}); // for ordering kind
  const [matchState, setMatchState] = useState({}); // for matching kind

  useEffect(() => {
    if (!participantAuth.getToken()) { nav("/experience"); return; }
    axios.get(`${API}/experience/wines/${wineId}/quiz`, {
      headers: { Authorization: `Bearer ${participantAuth.getToken()}` }
    }).then(r => setQuiz(r.data)).catch(() => { toast.error("Quiz indisponible"); nav("/experience/my"); });
  }, [wineId, nav]);

  if (!quiz) return <div className="p-6 text-center text-sm text-neutral-500">Chargement du quiz…</div>;
  const qs = quiz.questions || [];
  if (qs.length === 0) return <div className="p-6 text-center text-sm text-neutral-500">Aucune question.</div>;

  const q = qs[idx];

  const chooseQcm = (letter) => {
    setAnswers({ ...answers, [q.id]: letter });
    if (idx + 1 < qs.length) setIdx(idx + 1);
    else submit({ ...answers, [q.id]: letter });
  };

  const finishOrder = () => {
    const items = orderState[q.id] || q.items;
    setAnswers({ ...answers, [q.id]: JSON.stringify(items) });
    if (idx + 1 < qs.length) setIdx(idx + 1);
    else submit({ ...answers, [q.id]: JSON.stringify(items) });
  };

  const finishMatch = () => {
    const m = matchState[q.id] || {};
    setAnswers({ ...answers, [q.id]: JSON.stringify(m) });
    if (idx + 1 < qs.length) setIdx(idx + 1);
    else submit({ ...answers, [q.id]: JSON.stringify(m) });
  };

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const payload = {
        wine_event_id: wineId,
        course_slug: quiz.course_slug || null,
        answers: Object.entries(finalAnswers).map(([qid, a]) => ({ question_id: qid, answer: a })),
      };
      const r = await axios.post(`${API}/experience/wines/quiz-submit`, payload, {
        headers: { Authorization: `Bearer ${participantAuth.getToken()}` }
      });
      setResult(r.data);
      toast.success(`Score: ${r.data.correct}/${r.data.total} · +${r.data.points_awarded} pts !`);
    } catch (e) { toast.error("Erreur"); } finally { setSubmitting(false); }
  };

  if (result) {
    const perfect = result.perfect;
    return (
      <div className="max-w-xl mx-auto px-4 py-8 text-center">
        <Trophy className={`w-16 h-16 mx-auto mb-3 ${perfect ? "text-yellow-500" : "text-neutral-400"}`} />
        <h1 className="text-2xl font-display mb-2 text-[#1F3A5F]">{perfect ? "Sans faute !" : "Bien joué !"}</h1>
        <p className="text-3xl font-bold text-[#E5276A] my-3">{result.correct} / {result.total}</p>
        <p className="text-sm text-neutral-600 mb-4">+{result.points_awarded} points ajoutés à votre score</p>
        <Button className="bg-[#E5276A] hover:bg-[#C01661]" onClick={() => nav("/experience/my")}>
          Retour aux vins
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-6">
      <button onClick={() => nav("/experience/my")} className="text-sm text-neutral-600 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-[#E5276A]">Question {idx + 1}/{qs.length}</div>
        <div className="text-xs text-neutral-500">{quiz.source === "academy" ? "🎓 Academy" : quiz.source === "custom" ? "🎯 Perso" : "Quiz"}</div>
      </div>
      <div className="w-full bg-neutral-200 rounded-full h-1.5 mb-4">
        <div className="bg-[#E5276A] h-1.5 rounded-full transition-all" style={{ width: `${((idx) / qs.length) * 100}%` }} />
      </div>

      <h2 className="text-lg sm:text-xl font-display mb-4">{q.question}</h2>

      {q.kind === "qcm" && (
        <div className="grid gap-2">
          {(q.choices || []).map((opt, i) => {
            const letter = String.fromCharCode(65 + i);
            const m = opt.match(/^([A-D])\.\s*(.*)$/);
            const label = m ? m[2] : opt;
            const key = m ? m[1] : letter;
            return (
              <Card key={i} data-testid={`quiz-opt-${key}`} className="cursor-pointer hover:border-[#E5276A] transition"
                    onClick={() => chooseQcm(key)}>
                <CardContent className="p-3 text-sm flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#E5276A]/10 flex items-center justify-center text-[#E5276A] font-bold">{key}</span>
                  <span>{label}</span>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {q.kind === "ordering" && (
        <OrderingUI q={q} state={orderState[q.id] || q.items}
          setState={(v) => setOrderState({ ...orderState, [q.id]: v })}
          onSubmit={finishOrder} />
      )}
      {q.kind === "matching" && (
        <MatchingUI q={q} state={matchState[q.id] || {}}
          setState={(v) => setMatchState({ ...matchState, [q.id]: v })}
          onSubmit={finishMatch} />
      )}
      {submitting && <p className="text-center text-xs mt-3">Enregistrement…</p>}
    </div>
  );
}

function OrderingUI({ q, state, setState, onSubmit }) {
  const move = (i, dir) => {
    const arr = [...state];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setState(arr);
  };
  return (
    <div>
      <div className="grid gap-2 mb-4">
        {state.map((item, i) => (
          <Card key={item + i} className="border-neutral-200">
            <CardContent className="p-3 flex items-center gap-2 text-sm">
              <span className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-xs font-bold">{i + 1}</span>
              <span className="flex-1">{item}</span>
              <Button size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}>↑</Button>
              <Button size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === state.length - 1}>↓</Button>
            </CardContent>
          </Card>
        ))}
      </div>
      <Button onClick={onSubmit} className="w-full bg-[#E5276A] hover:bg-[#C01661]">Valider</Button>
    </div>
  );
}

function MatchingUI({ q, state, setState, onSubmit }) {
  const lefts = q.pairs_left || [];
  const rights = q.pairs_right_pool || [];
  return (
    <div>
      <div className="grid gap-2 mb-4">
        {lefts.map((l) => (
          <div key={l} className="flex items-center gap-2">
            <div className="flex-1 text-sm">{l}</div>
            <select
              className="border rounded-md px-2 py-1.5 text-sm bg-white"
              value={state[l] || ""}
              onChange={(e) => setState({ ...state, [l]: e.target.value })}
            >
              <option value="">Choisir…</option>
              {rights.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        ))}
      </div>
      <Button onClick={onSubmit} disabled={Object.keys(state).length < lefts.length}
              className="w-full bg-[#E5276A] hover:bg-[#C01661]">Valider</Button>
    </div>
  );
}
